// Statement Templates designer helpers: ids, cloning, element / column factories, catalog lookups and the
// undo history. The template shapes are in types/formattedSoa.ts; the server normalises every definition
// (backend utils/soaTemplate.ts), so what is built here stays within those rules.
import { useCallback, useRef, useState } from 'react';
import {
  SoaBandKey, SoaBorderSide, SoaColumn, SoaElement, SoaElementType, SoaFieldDef, SoaFieldType, SoaStyle,
  SoaTemplateDefinition,
} from '../types/formattedSoa';

/** Screen pixels per millimetre at 100 % (96 dpi). */
export const PX_PER_MM = 96 / 25.4;

/** Converts pt to on-screen px at the given zoom (px per mm). */
export const ptToPx = (pt: number, zoom: number): number => (pt * 25.4) / 72 * zoom;

let counter = 0;
/** An element / column id the server accepts (^[A-Za-z0-9_-]{1,40}$). */
export function newId(prefix = 'e'): string {
  counter = (counter + 1) % 46656;
  return `${prefix}${Date.now().toString(36).slice(-6)}${counter.toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
}

export function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export const round2 = (n: number): number => Math.round(n * 100) / 100;

export function snapTo(n: number, grid: number | null): number {
  if (!grid) return round2(n);
  return round2(Math.round(n / grid) * grid);
}

export const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

export const BAND_KEYS: SoaBandKey[] = ['pageHeader', 'reportHeader', 'reportFooter', 'pageFooter'];

/** What the designer has selected: an element, a table column, a band, the table, or nothing. */
export type SoaSelection =
  | { kind: 'element'; band: SoaBandKey; id: string }
  | { kind: 'column'; id: string }
  | { kind: 'band'; band: SoaBandKey }
  | { kind: 'table' }
  | null;

/** Where a field dragged from the field list is dropped. */
export type SoaDropTarget =
  | { kind: 'band'; band: SoaBandKey; x: number; y: number }
  | { kind: 'table'; index: number };

/** Drag-and-drop data types of the field list (the type, not the data, is readable while dragging). */
export const DND_FIELD = 'application/x-soa-field';
export const DND_ROW_FIELD = 'application/x-soa-row-field';

export const BAND_SHORT: Record<SoaBandKey, string> = {
  pageHeader: 'Page header',
  reportHeader: 'Report header',
  reportFooter: 'Report footer',
  pageFooter: 'Page footer',
};

export const ELEMENT_LABELS: Record<SoaElementType, string> = {
  text: 'Text',
  field: 'Field',
  image: 'Image',
  line: 'Line',
  box: 'Box',
  kv: 'Label / value table',
  html: 'HTML',
};

export const GROUP_LABELS: Record<string, string> = {
  company: 'Company',
  bp: 'Business partner',
  statement: 'Statement',
  totals: 'Totals',
  page: 'Page',
  row: 'Table columns (transactions)',
};

export const GROUP_ORDER = ['company', 'bp', 'statement', 'totals', 'page', 'row'];

/** SAP transaction types the Settings tab names (the document codes map). */
export const TRANS_TYPE_NAMES: Record<string, string> = {
  '13': 'A/R Invoice',
  '14': 'A/R Credit Memo',
  '203': 'A/R Down Payment',
  '24': 'Incoming Payment',
  '18': 'A/P Invoice',
  '19': 'A/P Credit Memo',
  '204': 'A/P Down Payment',
  '46': 'Outgoing Payment',
  '30': 'Journal Entry',
  '321': 'Internal Reconciliation',
  '-2': 'Opening Balance',
  '-3': 'Closing Balance',
  '15': 'Delivery',
  '16': 'Return',
  '25': 'Deposit',
  '57': 'Checks for Payment',
  '69': 'Landed Costs',
};

export const solidSide = (width = 0.75, color = '#000000'): SoaBorderSide => ({ width, style: 'solid', color });

/** A new element of the given type at (x, y) mm, with sensible size and content. */
export function makeElement(type: SoaElementType, x: number, y: number, field?: SoaFieldDef): SoaElement {
  const base = { id: newId(type[0]), x: round2(x), y: round2(y), style: {} as SoaStyle };
  switch (type) {
    case 'text':
      return { ...base, type, w: 60, h: 5, text: 'Text', canGrow: true };
    case 'field':
      return {
        ...base, type, w: 60, h: 5, field: field?.key ?? 'bp.name', canGrow: true,
        format: field ? defaultFormat(field.type) : undefined,
      };
    case 'image':
      return { ...base, type, w: 40, h: 18, source: 'company.logo', fit: 'contain' };
    case 'line':
      return { ...base, type, w: 60, h: 0.5, orientation: 'horizontal', thickness: 0.75, color: '#000000', lineStyle: 'solid' };
    case 'box':
      return {
        ...base, type, w: 60, h: 20, radius: 0,
        style: { borders: { top: solidSide(), right: solidSide(), bottom: solidSide(), left: solidSide() } },
      };
    case 'kv':
      return {
        ...base, type, w: 100, h: 12, canGrow: true, labelWidth: 30,
        rows: [{ label: 'ACCOUNT CODE', value: '{{bp.code}}' }, { label: 'ACCOUNT NAME', value: '{{bp.name}}' }],
        labelStyle: { padding: 1.2, bold: true }, valueStyle: { padding: 1.2 }, gridColor: '#b9b9b9', gridWidth: 0.75,
      };
    case 'html':
    default:
      return { ...base, type: 'html', w: 80, h: 12, canGrow: true, html: '<b>{{bp.name}}</b>' };
  }
}

/** The format a field of that type gets when it is added. */
export function defaultFormat(type: SoaFieldType): string | undefined {
  if (type === 'date') return 'date';
  if (type === 'datetime') return 'datetime';
  if (type === 'amount') return 'amount';
  return undefined;
}

/** A new table column for a row field. */
export function makeColumn(field: SoaFieldDef): SoaColumn {
  const isAmount = field.type === 'amount';
  const isDate = field.type === 'date' || field.type === 'datetime';
  return {
    id: newId('c'),
    field: field.key,
    header: field.label.toUpperCase().slice(0, 40),
    width: isAmount ? 20 : isDate ? 18 : field.key === 'remarks' || field.key === 'journalMemo' ? 45 : 22,
    align: isAmount ? 'right' : isDate ? 'center' : 'left',
    format: defaultFormat(field.type),
    wrap: !isAmount && !isDate,
  };
}

/** Every element id in the template (to keep pasted ids unique). */
export function elementIds(def: SoaTemplateDefinition): Set<string> {
  const ids = new Set<string>();
  BAND_KEYS.forEach((k) => def.bands[k].elements.forEach((e) => ids.add(e.id)));
  return ids;
}

export function findElement(def: SoaTemplateDefinition, band: SoaBandKey, id: string): SoaElement | undefined {
  return def.bands[band].elements.find((e) => e.id === id);
}

/** The template with one element replaced (same band). */
export function updateElement(def: SoaTemplateDefinition, band: SoaBandKey, id: string, fn: (e: SoaElement) => SoaElement): SoaTemplateDefinition {
  return {
    ...def,
    bands: {
      ...def.bands,
      [band]: { ...def.bands[band], elements: def.bands[band].elements.map((e) => (e.id === id ? fn(e) : e)) },
    },
  };
}

export function updateColumn(def: SoaTemplateDefinition, id: string, fn: (c: SoaColumn) => SoaColumn): SoaTemplateDefinition {
  return { ...def, table: { ...def.table, columns: def.table.columns.map((c) => (c.id === id ? fn(c) : c)) } };
}

/** Grows the band when an element reaches below it (the designer's view of "the band holds its content"). */
export function fitBandToElements(def: SoaTemplateDefinition, band: SoaBandKey): SoaTemplateDefinition {
  const b = def.bands[band];
  const bottom = b.elements.reduce((m, e) => Math.max(m, e.y + (e.type === 'line' && e.orientation === 'horizontal' ? 0 : e.h)), 0);
  if (bottom <= b.height) return def;
  return { ...def, bands: { ...def.bands, [band]: { ...b, height: round2(bottom) } } };
}

/** Label of a catalog key, e.g. «Company name». */
export function fieldLabel(fields: Map<string, SoaFieldDef>, key: string): string {
  return fields.get(key)?.label ?? key;
}

/** The text a field shows on the canvas. */
export function fieldPreview(fields: Map<string, SoaFieldDef>, key: string): string {
  const f = fields.get(key);
  if (!f) return `{{${key}}}`;
  if (f.type === 'image') return 'LOGO';
  return `«${f.label}»`;
}

/** A placeholder for a text element: {{key}} or {{key|format}}. */
export function placeholder(key: string, format?: string): string {
  return format ? `{{${key}|${format}}}` : `{{${key}}}`;
}

/** Formats a date pattern with a fixed example date (31 Dec 2025 23:59:58). */
export function examplePattern(pattern: string): string {
  return formatDatePattern('2025-12-31T23:59:58', pattern);
}

/** 'YYYY-MM-DD[THH:mm:ss]' in a pattern of the tokens DD D MM M MMM MMMM YY YYYY HH mm ss (as the renderer). */
export function formatDatePattern(value: string, pattern: string): string {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
  if (!m) return value;
  const p = { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : 0, mi: m[5] ? +m[5] : 0, s: m[6] ? +m[6] : 0 };
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return pattern.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|mm|ss/g, (tok) => {
    switch (tok) {
      case 'YYYY': return String(p.y);
      case 'YY': return pad(p.y % 100);
      case 'MMMM': return months[p.mo - 1];
      case 'MMM': return months[p.mo - 1].slice(0, 3);
      case 'MM': return pad(p.mo);
      case 'M': return String(p.mo);
      case 'DD': return pad(p.d);
      case 'D': return String(p.d);
      case 'HH': return pad(p.h);
      case 'mm': return pad(p.mi);
      case 'ss': return pad(p.s);
      default: return tok;
    }
  });
}

/** An amount as the template prints it: decimals, thousands, negatives, zero, Dr / Cr. */
export function formatSampleAmount(n: number, format: string | undefined, def: SoaTemplateDefinition, companyDecimals = 3): string {
  const [name, arg] = (format || 'amount').split(':');
  const f = def.formats;
  const decimals = arg !== undefined && arg !== '' ? Number(arg) : f.amountDecimals ?? companyDecimals;
  if (name === 'number') return String(n);
  let abs = Math.abs(n);
  if (abs < 0.5 * 10 ** -decimals) {
    if (f.zero === 'blank') return '';
    if (f.zero === 'dash') return '-';
    abs = 0;
  }
  let s = abs.toFixed(decimals);
  if (f.thousands) {
    const parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    s = parts.join('.');
  }
  if (name === 'drcr') return `${s}${n > 0 ? ' Dr' : n < 0 ? ' Cr' : ''}`;
  if (name === 'abs' || n >= 0 || abs === 0) return s;
  return f.negative === 'parentheses' ? `(${s})` : `-${s}`;
}

/** `over` on top of `base`, borders side by side (as the renderer merges styles). */
export function mergeStyle(base: SoaStyle | undefined, over: SoaStyle | undefined): SoaStyle {
  const out: SoaStyle = { ...(base ?? {}) };
  for (const [k, v] of Object.entries(over ?? {})) {
    if (v !== undefined && k !== 'borders') (out as Record<string, unknown>)[k] = v;
  }
  if (base?.borders || over?.borders) out.borders = { ...(base?.borders ?? {}), ...(over?.borders ?? {}) };
  return out;
}

/** CSS font stack for the canvas (the printed stacks are on the server). */
export function canvasFont(name: string | undefined, fallback: string): string {
  const n = name || fallback;
  return `"${n}", Arial, "Noto Sans Arabic", sans-serif`;
}

// ── undo history ─────────────────────────────────────────────────────────────────────────────

interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

const HISTORY_LIMIT = 120;
const MERGE_MS = 1200;

/**
 * State with undo / redo. Changes that share a `mergeKey` within a short time (a drag, typing in one box)
 * become one undo step.
 */
export function useUndoable<T>(initial: T) {
  const [state, setState] = useState<HistoryState<T>>({ past: [], present: initial, future: [] });
  const last = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });

  const update = useCallback((fn: (prev: T) => T, mergeKey?: string) => {
    const now = Date.now();
    const merge = !!mergeKey && last.current.key === mergeKey && now - last.current.at < MERGE_MS;
    last.current = { key: mergeKey ?? null, at: now };
    setState((s) => {
      const next = fn(s.present);
      if (next === s.present) return s;
      if (merge) return { past: s.past, present: next, future: [] };
      return { past: [...s.past, s.present].slice(-HISTORY_LIMIT), present: next, future: [] };
    });
  }, []);

  const undo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setState((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s));
  }, []);

  const redo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setState((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s));
  }, []);

  /** A new starting point (after loading another template), clearing the history. */
  const reset = useCallback((value: T) => {
    last.current = { key: null, at: 0 };
    setState({ past: [], present: value, future: [] });
  }, []);

  /** Swaps the present value without an undo step (the server's normalised copy after a save). */
  const replace = useCallback((value: T) => {
    last.current = { key: null, at: 0 };
    setState((s) => ({ ...s, present: value }));
  }, []);

  return {
    value: state.present, update, undo, redo, reset, replace, canUndo: state.past.length > 0, canRedo: state.future.length > 0,
  };
}
